import React, { useEffect, useState } from "react";
import { Outlet, NavLink } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store";
import { initializeApp } from "../../store/thunks/sessionThunks";
import { clearGlobalError } from "../../store/slices/appSlice";
import {
  selectIsInitialized,
  selectGlobalError,
  selectGlobalLoading,
} from "../../store/selectors";
import Navbar from "../Navbar/Navbar";
import "./Layout.css";
import BreadCrumbs from "../BreadCrumbs/BreadCrumbs";
import { Box } from "@mui/material";
import ModeToggle from "../Navbar/ModeToggle";
import { MenuIconDisplay, MobileNavabar } from "../Navbar/MobileNavabar";

export const Layout: React.FC = () => {
  const dispatch = useAppDispatch();
  const isInitialized = useAppSelector(selectIsInitialized);
  const globalError = useAppSelector(selectGlobalError);
  const isLoading = useAppSelector(selectGlobalLoading);

  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  // Initialize app on mount
  useEffect(() => {
    dispatch(initializeApp());
  }, [dispatch]);

  // Show loading state during initialization
  if (!isInitialized && isLoading) {
    return (
      <div className="layout">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          <div
            className="loading-spinner"
            style={{
              width: "40px",
              height: "40px",
              border: "3px solid #f3f3f3",
              borderTop: "3px solid #007bff",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          ></div>
          <p>Loading Patient Pro...</p>
        </div>
      </div>
    );
  }

  // Show error state if initialization failed
  if (!isInitialized && globalError) {
    return (
      <div className="layout">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            flexDirection: "column",
            gap: "1rem",
            padding: "2rem",
            textAlign: "center",
          }}
        >
          <div
            style={{
              background: "#f8d7da",
              color: "#721c24",
              padding: "1.5rem",
              borderRadius: "8px",
              border: "1px solid #f5c6cb",
              maxWidth: "500px",
            }}
          >
            <h3>Failed to Initialize Application</h3>
            <p style={{ margin: "1rem 0" }}>{globalError}</p>
            <button
              onClick={() => {
                dispatch(clearGlobalError());
                dispatch(initializeApp());
              }}
              style={{
                background: "#007bff",
                color: "white",
                border: "none",
                padding: "0.75rem 1.5rem",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "1rem",
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile Navbar - rendered outside main layout for proper overlay */}
      <MobileNavabar isOpen={isMobileNavOpen} setIsOpen={setIsMobileNavOpen} />
      
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          height: "100vh",
          overflow: "hidden",
        }}
      >
        {/* Navbar */}
        <Box className="navbar-container ">
          <Navbar />
        </Box>
        {/* Main Content */}
        <Box
          sx={{
            flex: "1",
            overflowY: "auto",
          }}
        >
          <Box
            className="breadcrumbs-container"
            sx={{
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <BreadCrumbs />
            <ModeToggle />
            <MenuIconDisplay isOpen={isMobileNavOpen} setIsOpen={setIsMobileNavOpen} />
          </Box>
          <Box sx={{ padding: "35px" }}>
            <Outlet />
          </Box>
        </Box>
        {/* Global error banner */}
        {globalError && isInitialized && (
          <div
            style={{
              background: "#f8d7da",
              color: "#721c24",
              padding: "1rem",
              textAlign: "center",
              borderBottom: "1px solid #f5c6cb",
            }}
          >
            <strong>Error:</strong> {globalError}
            <button
              onClick={() => dispatch(clearGlobalError())}
              style={{
                marginLeft: "1rem",
                background: "none",
                border: "none",
                color: "inherit",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              Dismiss
            </button>
          </div>
        )}
      </Box>
    </>
  );
};
